import { t } from "i18next";

import { useApplicationStatus } from "../features/review/api/useApplicationStatus";
import { parseRolePermissions } from "../lib/permissions/parseRolePermissions";
import { resolvePanelPermissions, type WorkbenchPanelPermissions } from "../lib/permissions/resolvePanelPermissions";
import type { EffectiveSectionPermission } from "../lib/permissions/resolveSectionPermission";
import { resolveAllSectionPermissions } from "../lib/permissions/resolveSectionPermission";
import type { WorkbenchSectionId } from "../lib/permissions/types";
import { useLockReadOnly } from "../stores/approvalStore";
import { useRolePermissions } from "./useRolePermissions";

export interface UseWorkbenchPermissionsResult {
  /** Final, ready-to-render per-section verdicts — combines role + deal status/phase + VLA lock. */
  sections: Record<WorkbenchSectionId, EffectiveSectionPermission>;
  panels: WorkbenchPanelPermissions;
  /** The role's `EXIT` marker — see `WorkbenchRoleCapabilities.canExit`. Not gated by deal state or lock. */
  canExit: boolean;
  isCacUser: boolean;
  /** True while role capabilities or application status are still loading. Every section reports hidden/read-only meanwhile (fail-safe). */
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * The single source of truth for "what can this user do on this section right
 * now" — combines the three permission layers per the required precedence
 * (**VLA Lock → Status/Phase → Role**, see `resolveSectionPermission`):
 *
 * 1. **Role** — `useRolePermissions()` (parsed from `ProgramRoleCapabilitiesApi`).
 * 2. **Status/Phase** — `useApplicationStatus(approvalId)` fed through
 *    `getDealStateVerdictForSection` (matrix §4.1–§4.4, §5.1–§5.5).
 * 3. **VLA Lock** — `useLockReadOnly()` (existing lock store, reused as-is).
 *
 * Fails safe: while either query is loading, or if either errors, every section
 * is reported hidden/read-only rather than optimistically editable.
 * @param {string} approvalId - The deal / approval identifier whose status drives the deal-state layer.
 * @returns {UseWorkbenchPermissionsResult} Combined per-section verdicts plus loading/error state.
 */
export function useWorkbenchPermissions(approvalId: string): UseWorkbenchPermissionsResult {
  const role = useRolePermissions();
  const status = useApplicationStatus(approvalId);
  const isLockReadOnly = useLockReadOnly();

  const isLoading = role.isLoading || status.isLoading;
  const statusError =
    status.isSuccess && !status.data?.statusDescription?.trim()
      ? new Error(t("workbench.statusUnavailable"))
      : undefined;
  const isError = role.isError || status.isError || !!statusError;

  const ready = !isLoading && !isError && !!status.data?.statusDescription;
  const sections = resolveAllSectionPermissions(
    ready ? role.capabilities : parseRolePermissions({ permissions: [] }),
    ready ? status.data : undefined,
    !isLockReadOnly
  );

  return {
    sections,
    panels: resolvePanelPermissions(role.capabilities, status.data, !isLockReadOnly, ready),
    canExit: role.capabilities.canExit,
    isCacUser: ready && role.capabilities.isCacUser,
    isLoading,
    isError,
    error: role.error ?? status.error ?? statusError,
  };
}
